import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chainApi, type ChainHospital } from "@/api/chain";
import type { TenantBranding } from "@/api/chain";
import { TenantBrandingFields } from "@/components/chain/TenantBrandingFields";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  brandingFromRow,
  brandingToPayload,
  emptyBrandingForm,
} from "@/lib/tenantBranding";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

function hasBrandingOverride(h: ChainHospital): boolean {
  return !!(
    h.logoUrl ||
    h.primaryColor ||
    h.secondaryColor ||
    h.accentColor ||
    h.sidebarBgColor ||
    h.customUiColors != null
  );
}

export default function ChainHospitalsPage() {
  const qc = useQueryClient();
  const { activeHospitalId, refreshBranding } = useAuth();
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<ChainHospital | null>(null);
  const [form, setForm] = useState({
    name: "",
    unitCode: "",
    address: "",
    contactNumber: "",
    email: "",
    gstin: "",
    mapsUrl: "",
    googlePlaceId: "",
    ipdFeedbackSheetUrl: "",
    postSurgeryContact1Name: "",
    postSurgeryContact1Phone: "",
    postSurgeryContact2Name: "",
    postSurgeryContact2Phone: "",
  });
  const [editForm, setEditForm] = useState({
    name: "",
    unitCode: "",
    address: "",
    contactNumber: "",
    email: "",
    gstin: "",
    mapsUrl: "",
    googlePlaceId: "",
    ipdFeedbackSheetUrl: "",
    postSurgeryContact1Name: "",
    postSurgeryContact1Phone: "",
    postSurgeryContact2Name: "",
    postSurgeryContact2Phone: "",
    useTenantBranding: true,
    branding: emptyBrandingForm(),
  });

  const { data: group, isLoading } = useQuery({
    queryKey: ["chain-group"],
    queryFn: async () => {
      const r = await chainApi.getGroup();
      return r.data.data as {
        name: string;
        maxHospitals: number;
        usage: { hospitalCount: number; userCount: number };
        hospitals: ChainHospital[];
      };
    },
  });

  useEffect(() => {
    const name = form.name.trim();
    if (!name || name.length < 2) return;
    const handle = window.setTimeout(() => {
      chainApi
        .suggestUnitCode(name)
        .then((r) => {
          const data = r.data.data as { unitCode: string };
          setForm((f) => ({
            ...f,
            unitCode: f.unitCode.trim() ? f.unitCode : data.unitCode,
          }));
        })
        .catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [form.name]);

  useEffect(() => {
    if (!editOpen || !editing || editing.unitCode) return;
    const name = editForm.name.trim();
    if (!name || name.length < 2) return;
    const handle = window.setTimeout(() => {
      chainApi
        .suggestUnitCode(name, editing.id)
        .then((r) => {
          const data = r.data.data as { unitCode: string };
          setEditForm((f) => ({
            ...f,
            unitCode: f.unitCode.trim() ? f.unitCode : data.unitCode,
          }));
        })
        .catch(() => undefined);
    }, 400);
    return () => window.clearTimeout(handle);
  }, [editOpen, editing, editForm.name]);

  const createMut = useMutation({
    mutationFn: () =>
      chainApi.createHospital({
        ...form,
        mapsUrl: form.mapsUrl.trim() || undefined,
        googlePlaceId: form.googlePlaceId.trim() || undefined,
        ipdFeedbackSheetUrl: form.ipdFeedbackSheetUrl.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success("Hospital created");
      setOpen(false);
      setForm({
        name: "",
        unitCode: "",
        address: "",
        contactNumber: "",
        email: "",
        gstin: "",
        mapsUrl: "",
        googlePlaceId: "",
        ipdFeedbackSheetUrl: "",
        postSurgeryContact1Name: "",
        postSurgeryContact1Phone: "",
        postSurgeryContact2Name: "",
        postSurgeryContact2Phone: "",
      });
      qc.invalidateQueries({ queryKey: ["chain-group"] });
      qc.invalidateQueries({ queryKey: ["chain-dashboard"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to create hospital");
    },
  });

  const updateMut = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("No hospital selected");
      const payload: Record<string, unknown> = {
        name: editForm.name.trim(),
        address: editForm.address.trim(),
        contactNumber: editForm.contactNumber.trim(),
        email: editForm.email.trim(),
        gstin: editForm.gstin.trim() || null,
      };
      if (editForm.useTenantBranding) {
        payload.logoUrl = null;
        payload.primaryColor = null;
        payload.secondaryColor = null;
        payload.accentColor = null;
        payload.sidebarBgColor = null;
        payload.customUiColors = null;
      } else {
        Object.assign(payload, brandingToPayload(editForm.branding, { allowNull: true }));
      }
      if (!editing.unitCode && editForm.unitCode.trim()) {
        payload.unitCode = editForm.unitCode.trim().toUpperCase();
      }
      payload.mapsUrl = editForm.mapsUrl.trim() || null;
      payload.googlePlaceId = editForm.googlePlaceId.trim() || null;
      payload.ipdFeedbackSheetUrl = editForm.ipdFeedbackSheetUrl.trim() || null;
      payload.postSurgeryContact1Name = editForm.postSurgeryContact1Name.trim() || null;
      payload.postSurgeryContact1Phone = editForm.postSurgeryContact1Phone.trim() || null;
      payload.postSurgeryContact2Name = editForm.postSurgeryContact2Name.trim() || null;
      payload.postSurgeryContact2Phone = editForm.postSurgeryContact2Phone.trim() || null;
      return chainApi.updateHospital(editing.id, payload);
    },
    onSuccess: async () => {
      toast.success("Hospital updated");
      setEditOpen(false);
      if (editing && editing.id === activeHospitalId) {
        await refreshBranding(editing.id);
      }
      setEditing(null);
      qc.invalidateQueries({ queryKey: ["chain-group"] });
    },
    onError: (e: { response?: { data?: { message?: string } } }) => {
      toast.error(e.response?.data?.message ?? "Failed to update hospital");
    },
  });

  const openEdit = (h: ChainHospital) => {
    setEditing(h);
    const override = hasBrandingOverride(h);
    setEditForm({
      name: h.name ?? "",
      unitCode: "",
      address: h.address ?? "",
      contactNumber: h.contactNumber ?? "",
      email: h.email ?? "",
      gstin: h.gstin ?? "",
      mapsUrl: h.mapsUrl ?? "",
      googlePlaceId: h.googlePlaceId ?? "",
      ipdFeedbackSheetUrl: h.ipdFeedbackSheetUrl ?? "",
      postSurgeryContact1Name: h.postSurgeryContact1Name ?? "",
      postSurgeryContact1Phone: h.postSurgeryContact1Phone ?? "",
      postSurgeryContact2Name: h.postSurgeryContact2Name ?? "",
      postSurgeryContact2Phone: h.postSurgeryContact2Phone ?? "",
      useTenantBranding: !override,
      branding: override
        ? brandingFromRow(h)
        : emptyBrandingForm(),
    });
    setEditOpen(true);
  };

  const hospitals = group?.hospitals ?? [];

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">My hospitals</h1>
          {group && (
            <p className="text-sm text-muted-foreground mt-1">
              {group.usage.hospitalCount} / {group.maxHospitals} hospitals in {group.name}. Edit a hospital to
              override tenant branding for that site.
            </p>
          )}
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button>Add hospital</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>New hospital</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {(["name", "address", "contactNumber", "email", "gstin"] as const).map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k === "contactNumber" ? "Contact" : k}</Label>
                  <Input
                    value={form[k]}
                    onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                  />
                </div>
              ))}
              <div>
                <Label>Google Maps URL</Label>
                <Input
                  value={form.mapsUrl}
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                  onChange={(e) => setForm((f) => ({ ...f, mapsUrl: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Share link from Google Maps — used in WhatsApp directions and GMB rating lookup.
                </p>
              </div>
              <div>
                <Label>Google Place ID (GMB rating)</Label>
                <Input
                  value={form.googlePlaceId}
                  placeholder="ChIJ..."
                  className="font-mono"
                  onChange={(e) => setForm((f) => ({ ...f, googlePlaceId: e.target.value.trim() }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. Paste Place ID from Google Maps for Business Reports GMB rating tile.
                </p>
              </div>
              <div>
                <Label>IPD feedback Google Sheet (NPS)</Label>
                <Input
                  value={form.ipdFeedbackSheetUrl}
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  onChange={(e) => setForm((f) => ({ ...f, ipdFeedbackSheetUrl: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Optional. IPD form responses sheet — share as &quot;Anyone with the link can view&quot;. Powers
                  Customer Voice and Business Reports NPS for this hospital.
                </p>
              </div>
              <div className="rounded-md border p-3 space-y-3">
                <div>
                  <Label className="font-medium">Post Surgery contact points</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. Shown at the end of the IPD discharge summary PDF for post-discharge calls.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Contact 1 — name</Label>
                    <Input
                      value={form.postSurgeryContact1Name}
                      onChange={(e) => setForm((f) => ({ ...f, postSurgeryContact1Name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Contact 1 — phone</Label>
                    <Input
                      value={form.postSurgeryContact1Phone}
                      inputMode="tel"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          postSurgeryContact1Phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Contact 2 — name</Label>
                    <Input
                      value={form.postSurgeryContact2Name}
                      onChange={(e) => setForm((f) => ({ ...f, postSurgeryContact2Name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label>Contact 2 — phone</Label>
                    <Input
                      value={form.postSurgeryContact2Phone}
                      inputMode="tel"
                      onChange={(e) =>
                        setForm((f) => ({
                          ...f,
                          postSurgeryContact2Phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                        }))
                      }
                    />
                  </div>
                </div>
              </div>
              <div>
                <Label>Unit code (6 characters)</Label>
                <Input
                  value={form.unitCode}
                  maxLength={6}
                  className="uppercase font-mono tracking-widest"
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      unitCode: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6),
                    }))
                  }
                  placeholder="e.g. TRUEAR"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Unique within your chain. Prepended before OPD, IPD, queue type codes and year/sequence
                  (e.g. TRUEAR + IPQ + 26 + 000001). Existing sites keep their current format.
                </p>
                {form.unitCode.length === 6 && (
                  <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 font-mono">
                    {(() => {
                      const yy = new Date().getFullYear().toString().slice(-2);
                      const u = form.unitCode;
                      return (
                        <>
                          <li>OPD: {u}OPD{yy}000001</li>
                          <li>IPD: {u}IPD{yy}000001</li>
                          <li>Queue: {u}IPQ{yy}000001</li>
                          <li>Pharma bill: PHA{u}000001</li>
                        </>
                      );
                    })()}
                  </ul>
                )}
              </div>
              <Button
                className="w-full"
                disabled={createMut.isPending || form.unitCode.length !== 6}
                onClick={() => createMut.mutate()}
              >
                Create
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit hospital — {editing?.name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              {(["name", "address", "contactNumber", "email", "gstin"] as const).map((k) => (
                <div key={k}>
                  <Label className="capitalize">{k === "contactNumber" ? "Contact" : k}</Label>
                  <Input
                    value={editForm[k]}
                    onChange={(e) => setEditForm((f) => ({ ...f, [k]: e.target.value }))}
                  />
                </div>
              ))}

              {editing.unitCode ? (
                <div>
                  <Label>Unit code</Label>
                  <Input value={editing.unitCode} readOnly className="font-mono uppercase bg-muted" />
                  <p className="text-xs text-muted-foreground mt-1">
                    Set permanently — new OPD, IPD, queue, and bill numbers use this prefix.
                  </p>
                </div>
              ) : (
                <div>
                  <Label>Unit code (6 characters)</Label>
                  <Input
                    value={editForm.unitCode}
                    maxLength={6}
                    className="uppercase font-mono tracking-widest"
                    onChange={(e) =>
                      setEditForm((f) => ({
                        ...f,
                        unitCode: e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, 6),
                      }))
                    }
                    placeholder="e.g. TRUESD"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional — leave blank to keep legacy numbering for now. Once saved, it cannot be
                    changed. Applies to <strong>new</strong> admissions and bills only; existing IDs stay
                    as-is.
                  </p>
                  {editForm.unitCode.length === 6 && (
                    <ul className="text-xs text-muted-foreground mt-2 space-y-0.5 font-mono">
                      {(() => {
                        const yy = new Date().getFullYear().toString().slice(-2);
                        const u = editForm.unitCode;
                        return (
                          <>
                            <li>OPD: {u}OPD{yy}000001</li>
                            <li>IPD: {u}IPD{yy}000001</li>
                            <li>Queue: {u}IPQ{yy}000001</li>
                            <li>Pharma bill: PHA{u}000001</li>
                          </>
                        );
                      })()}
                    </ul>
                  )}
                </div>
              )}

              <div>
                <Label>Google Maps URL</Label>
                <Input
                  value={editForm.mapsUrl}
                  type="url"
                  placeholder="https://maps.app.goo.gl/..."
                  onChange={(e) => setEditForm((f) => ({ ...f, mapsUrl: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Share link from the Lajpat Nagar / centre Google listing — WhatsApp directions and GMB lookup.
                </p>
              </div>
              <div>
                <Label>Google Place ID (GMB rating)</Label>
                <Input
                  value={editForm.googlePlaceId}
                  placeholder="ChIJ..."
                  className="font-mono"
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, googlePlaceId: e.target.value.trim() }))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  From Google Maps → Share → copy link or Place ID. Powers the Business Reports GMB rating tile for
                  this centre only.
                </p>
              </div>
              <div>
                <Label>IPD feedback Google Sheet (NPS)</Label>
                <Input
                  value={editForm.ipdFeedbackSheetUrl}
                  type="url"
                  placeholder="https://docs.google.com/spreadsheets/d/..."
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, ipdFeedbackSheetUrl: e.target.value }))
                  }
                />
                <p className="text-xs text-muted-foreground mt-1">
                  IPD Google Form linked sheet for this centre. Leave blank to use the tenant default sheet (with
                  IPD/phone row matching).
                </p>
              </div>

              <div className="rounded-md border p-3 space-y-3">
                <div>
                  <Label className="font-medium">Post Surgery contact points</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Optional. Shown at the end of the IPD discharge summary PDF for post-discharge calls.
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>Contact 1 — name</Label>
                    <Input
                      value={editForm.postSurgeryContact1Name}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, postSurgeryContact1Name: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Contact 1 — phone</Label>
                    <Input
                      value={editForm.postSurgeryContact1Phone}
                      inputMode="tel"
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          postSurgeryContact1Phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                        }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Contact 2 — name</Label>
                    <Input
                      value={editForm.postSurgeryContact2Name}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, postSurgeryContact2Name: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <Label>Contact 2 — phone</Label>
                    <Input
                      value={editForm.postSurgeryContact2Phone}
                      inputMode="tel"
                      onChange={(e) =>
                        setEditForm((f) => ({
                          ...f,
                          postSurgeryContact2Phone: e.target.value.replace(/\D/g, "").slice(0, 15),
                        }))
                      }
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox
                  id="use-tenant-branding"
                  checked={editForm.useTenantBranding}
                  onCheckedChange={(v) =>
                    setEditForm((f) => ({ ...f, useTenantBranding: !!v }))
                  }
                />
                <div className="space-y-1">
                  <Label htmlFor="use-tenant-branding" className="cursor-pointer">
                    Use tenant default branding
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Uncheck to set a custom logo and colors for this hospital only.
                  </p>
                </div>
              </div>

              {!editForm.useTenantBranding && (
                <TenantBrandingFields
                  value={editForm.branding}
                  onChange={(branding: TenantBranding) =>
                    setEditForm((f) => ({ ...f, branding }))
                  }
                  logoUpload={
                    editing ? { kind: "hospital", hospitalId: editing.id } : undefined
                  }
                  inheritHint
                />
              )}

              <Button
                className="w-full"
                disabled={
                  updateMut.isPending ||
                  (!editing.unitCode &&
                    editForm.unitCode.length > 0 &&
                    editForm.unitCode.length !== 6)
                }
                onClick={() => updateMut.mutate()}
              >
                Save changes
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Unit code</TableHead>
              <TableHead>Address</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Branding</TableHead>
              <TableHead className="w-[80px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {hospitals.map((h) => (
              <TableRow key={h.id}>
                <TableCell className="font-medium">{h.name}</TableCell>
                <TableCell className="font-mono text-sm">
                  {h.unitCode ?? (
                    <span className="text-muted-foreground text-xs">Legacy</span>
                  )}
                </TableCell>
                <TableCell>{h.address}</TableCell>
                <TableCell>{h.contactNumber}</TableCell>
                <TableCell>{h.status}</TableCell>
                <TableCell>
                  {hasBrandingOverride(h) ? (
                    <span className="text-xs text-muted-foreground">Custom override</span>
                  ) : (
                    <span className="text-xs text-muted-foreground">Tenant default</span>
                  )}
                </TableCell>
                <TableCell>
                  <Button variant="ghost" size="icon" onClick={() => openEdit(h)} aria-label="Edit hospital">
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
